import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { AppErrors } from '@fitness-force/errors';
import {
  ProductCategoryResponse,
  CreateProductCategoryRequest,
  UpdateProductCategoryRequest,
  ListProductCategoriesResponse,
  ListProductCategoriesRequest,
  CreateProductCategoryResponse,
  UpdateProductCategoryResponse
} from '../models';
import { environment } from 'apps/ff-frontend/src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ProductCategoryService {
  private readonly API_BASE_URL = environment.RETAIL_BILLING_URL;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(private http: HttpClient) {}

  private buildParams(params: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();
    
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== null && value !== undefined) {
        httpParams = httpParams.set(key, value.toString());
      }
    });
    
    return httpParams;
  }

  // Company-level category operations
  getCompanyCategories(request?: ListProductCategoriesRequest): Observable<ListProductCategoriesResponse> {
    // Map the request to API-expected parameters
    const params: any = {
      PageNumber: request?.page || 1,
      PageSize: request?.page_size || 10,
      CompanyId: request?.company_id || 50, // Default to company ID 50
    };
    
    // Add optional search parameters
    if (request?.search_term) {
      params.SearchTerm = request.search_term;
    }
    
    return this.http.get<ListProductCategoriesResponse>(`${this.API_BASE_URL}/companies/product-categories`, {
      headers: this.httpOptions.headers,
      params: this.buildParams(params)
    }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Company Categories API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getCompanyCategory(categoryId: number): Observable<ProductCategoryResponse> {
    const params = {
      CompanyId: 50
    };

    return this.http.get<ProductCategoryResponse>(`${this.API_BASE_URL}/companies/product-categories/${categoryId}`, {
      headers: this.httpOptions.headers,
      params: this.buildParams(params)
    }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Company Category API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  createCompanyCategory(request: CreateProductCategoryRequest): Observable<CreateProductCategoryResponse> {
    return this.http.post<CreateProductCategoryResponse>(`${this.API_BASE_URL}/companies/product-categories`, request, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Create Company Category API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  updateCompanyCategory(categoryId: number, request: UpdateProductCategoryRequest): Observable<UpdateProductCategoryResponse> {
    return this.http.put<UpdateProductCategoryResponse>(`${this.API_BASE_URL}/companies/product-categories/${categoryId}`, request, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Update Company Category API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }
}